<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="fas fa-hourglass-half" class="q-mb-sm" /> {{ $t('general_test') }}
        </div>

        <q-alert type="warning" icon="fa fa-exclamation-triangle" v-if="hasResults">
            {{ $t('general_test_has_results') }}
        </q-alert>

        <div class="row gutter-sm q-mb-lg">
            <div class="col-xs-12 col-sm-12">
                <q-input v-model="title" :float-label="$t('title')" :error="$v.title.$error" @input="$v.title.$touch" :disabled="hasResults" />
            </div>

            <div class="col-xs-12 col-sm-12">
                <q-input v-model="description" :float-label="$t('introduction')" rows="1" :max-height="100" type="textarea" :error="$v.description.$error" @input="$v.description.$touch" :disabled="hasResults" />
            </div>
        </div>

        <q-table
            :data="questions"
            :columns="columns_questions"
            :pagination.sync="server_pagination"
            @request="request_questions"
            :loading="loading"
            row-key="id"
            :no-data-label="$t('no_data_avaliable')"
            :no-results-label="$t('no_results_found')"
            :loading-label="$t('loading_data')"
            :rows-per-page-label="$t('items_per_page')"
            :rows-per-page-options="[0]"
            binary-state-sort
            class="q-my-lg"
            >
            <template slot="top" slot-scope="props">
                <div class="q-table-title">{{ $t('questions') }}</div>
                <div class="col" />
                <q-btn color="secondary" icon="fas fa-plus" :label="$t('new_f')" @click="showQuestion(0)"  :disable="hasResults" />
            </template>

            <q-tr slot="header" slot-scope="props" :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                {{ $t(col.label) }}
                </q-th>
            </q-tr>

            <q-tr slot="body" slot-scope="props" :props="props">
                <q-td auto-width key="id" :props="props">
                    {{ props.row.id }}
                </q-td>

                <q-td key="title" :props="props">
                    {{ props.row.title }}
                </q-td>

                <q-td key="qty_answers" :props="props">
                    {{ props.row.qty_answers }}
                </q-td>

                <q-td auto-width key="actions" :props="props">
                    <q-btn flat round color="primary" icon="fas fa-pencil-alt" @click="showQuestion(props.row.actions)" :disabled="hasResults" />
                    <q-btn flat round color="negative" icon="far fa-trash-alt" @click="confirmRemove(props.row.actions)" :disable="hasResults" />
                </q-td>
            </q-tr>
        </q-table>

        <div class="row gutter-sm q-mb-lg">
            <div class="col-xs-12 col-sm-6">
                <q-input v-model="minimum_score" :float-label="$t('minimum_score')" type="number" :error="$v.minimum_score.$error" @input="$v.minimum_score.$touch" :disabled="hasResults" />
            </div>

            <div class="col-xs-12 col-sm-6">
                <q-datetime v-model="time_to_finish" :float-label="$t('time_to_finish')" type="time" format="HH:mm:ss"  :error="$v.time_to_finish.$error" @input="$v.time_to_finish.$touch" :disabled="hasResults" />
            </div>

            <div class="col-xs-12 col-sm-6">
                <q-datetime v-model="publish_date" type="date" :float-label="$t('publish_date')" format="DD/MM/YYYY" :disabled="hasResults" />
            </div>

            <div class="col-xs-12 col-sm-6">
                <q-datetime v-model="unpublish_date" type="date" :float-label="$t('unpublish_date')" format="DD/MM/YYYY" :disabled="hasResults" />
            </div>

            <div class="col-xs-12 col-sm-12">
                <q-select v-model="tag" :options="tags" :float-label="$t('tags')" :error="$v.tag.$error" @input="$v.tag.$touch" multiple chips filter autofocus-filter :disabled="hasResults" />
            </div>
        </div>

        <q-tabs v-model="tab" color="secondary" class="shadow-1">
            <q-tab slot="title" name="permissions" icon="fas fa-clipboard-check" :label="$t('permissions')" />
            <q-tab slot="title" name="groups" icon="fas fa-users" :label="$t('groups')" />

            <q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
                <div class="row gutter-sm">
                    <div class="col-xs-12 col-sm-12">
                        <q-select v-model="area" :options="areas" :float-label="$t('areas')" :error="$v.area.$error" @input="$v.area.$touch" multiple chips :disabled="hasResults" />
                    </div>

                    <div class="col-xs-12 col-sm-12">
                        <p class="caption">{{ $t('profiles') }}</p>

                        <div class="row gutter-sm">
                            <div class="col-xs-12 col-sm-12">
                                <q-checkbox v-model="all_profiles" :val="true" :label="$t('all')" @input="checkAll()" :disabled="hasResults" />
                            </div>

                            <div class="col-xs-12 col-sm-4" v-for="(item, index) in profiles" :key="index" :class="getErrorClass($v.profile.$error)">
                                <q-checkbox keep-color v-model="profile" :val="item.value" :label="item.label" @input="$v.profile.$touch" :color="getErrorColor($v.profile.$error)" :disabled="hasResults" />
                            </div>
                        </div>
                    </div>
                </div>
            </q-tab-pane>

            <q-tab-pane name="groups" class="q-px-lg q-pb-xl">
                <div class="row gutter-sm">
                    <div class="col-xs-12 col-sm-12">
                        <q-select v-model="user_group" :options="user_groups" :float-label="$t('groups')" :error="$v.user_group.$error" @input="$v.user_group.$touch" multiple chips :disabled="hasResults" />
                    </div>
                </div>
            </q-tab-pane>
        </q-tabs>

        <div class="row gutter-sm q-my-sm">
            <div class="col-xs-12 col-sm-12">
                <p class="caption">{{ $t('status') }}</p>
                <q-btn-toggle v-model="status" :toggle-color="getColorStatus" :options="statuses" :disable="disableStatus" />
            </div>
        </div>

        <div class="row gutter-sm justify-end q-mt-lg">
            <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                <q-btn color="tertiary" class="full-width" :label="$t('back')" icon="fas fa-chevron-left" @click="$router.push({ name: 'admin_general_tests' })" />
            </div>

            <div class="col-xs-12 col-sm-3" :class="getOrder('save')">
                <q-btn color="primary" class="full-width" :label="$t('save')" icon="far fa-save" @click="saveTest()" />
            </div>
        </div>

        <new-question :id_test="id_test" :id_question="id_question" :add_question="add_question" :has_results="hasResults" @returnToQuestions="returnToQuestions()" />

        <q-dialog v-model="confirm_remove" @ok="removeItem">
            <span slot="title">{{ $t('remove_item') }}</span>

            <span slot="message">{{ $t('confirm_remove_item') }}</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
                <q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
            </template>
        </q-dialog>
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
import { required, requiredIf, maxValue, minValue } from 'vuelidate/lib/validators'
import NewQuestion from 'components/admin/general-tests/NewQuestion'

export default {
    i18nOptions: { namespaces: ["common", "general_tests", "admin"] },

    data() {
        return {
            id_test: '',
            title: '',
            description: '',
            minimum_score: '',
            qty_questions: '',
            qty_user_results: '',
            time_to_finish: '',
            publish_date: '',
            unpublish_date: '',
            status: 2,
            statuses: [
                { label: this.$t('published'), value: 1 },
                { label: this.$t('unpublished'), value: 2 }
            ],
            area: [],
            areas: [],
            profile: [],
            profiles: [],
            tag: [],
            tags: [],
            user_group: [],
            user_groups: [],
            tab: 'permissions',
            all_profiles: false,
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: 0,
                descending: false,
                sortBy: null
            },
            questions: [],
            columns_questions: [
                {
                    name: 'id',
                    required: true,
                    label: '#',
                    align: 'left',
                    field: 'id',
                    sortable: false,
                },
                {
                    name: 'title',
                    required: true,
                    label: 'question',
                    align: 'left',
                    field: 'title',
                    sortable: false,
                },
                {
                    name: 'qty_answers',
                    required: true,
                    label: 'alternative_quantity',
                    align: 'left',
                    field: 'qty_answers',
                    sortable: false,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'left',
                    field: 'actions',
                    sortable: true,
                }
            ],
            loading: false,
            add_question: false,
            id_question: '',
            action_id: null,
            confirm_remove: false
        }
    },

    components: {
        'new-question': NewQuestion
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '80vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        },

        getColorStatus() {
            if (this.status == 1) return 'positive'
            else return 'negative'
        },

        hasResults() {
            if (this.qty_user_results > 0) {
                return true
            }
            else {
                return false
            }
        },

        disableStatus() {
            if (!this.title || !this.description || !this.tag || !this.time_to_finish || !this.minimum_score) {
                this.status = 2
                return true
            }
            else if (this.questions.length == 0) {
                this.status = 2
                return true
            }
            else {
                return false
            }
        }
    },

    methods: {
        saveTest(type) {
            this.$v.$touch()

            if (!this.$v.$error) {
                this.$q.loading.show()

                let fd = new FormData()
                fd.append('title', this.title)
                fd.append('description', this.description)
                if (this.minimum_score) fd.append('minimum_score', this.minimum_score)
                fd.append('time_to_finish', date.formatDate(this.time_to_finish, 'HH:mm:ss'))
                if (this.publish_date) fd.append('publish_date', date.formatDate(this.publish_date, 'yyyy-mm-dd'))
                if (this.unpublish_date) fd.append('unpublish_date', date.formatDate(this.unpublish_date, 'yyyy-mm-dd'))
                fd.append('status', this.status)

                this.tag.forEach(tag => {
                    fd.append('tags', tag)
                })

                this.area.forEach(area => {
                    fd.append('areas', area)
                })

                this.profile.forEach(profile => {
                    fd.append('profiles', profile)
                })

                this.user_group.forEach(user_group => {
                    fd.append('user_groups', user_group)
                })

                if (this.id_test != 0) {
                    this.$axios.put(`/admin_general_tests/general_tests/${this.id_test}/`, fd, this.getHeader)
                    .then(response => {
                        this.$q.loading.hide()
                        this.$v.$reset()

                        if (type == 'add_question') {
                            this.add_question = true
                        }
                        else {
                            this.$router.push({ name: 'admin_general_tests' })
                        }
                    })
                    .catch(e => {
                        this.$q.loading.hide()
                        console.log(e)
                    })
                }
                else {
                    this.$axios.post(`/admin_general_tests/general_tests/`, fd, this.getHeader)
                    .then(response => {
                        this.$q.loading.hide()
                        this.$v.$reset()

                        if (type == 'add_question') {
                            this.id_test = response.data.id
                            this.$router.push({ name: 'admin_general_test', params: { id: response.data.id } })
                            this.add_question = true
                        }
                        else {
                            this.$router.push({ name: 'admin_general_tests' })
                        }
                    })
                    .catch(e => {
                        this.$q.loading.hide()
                        console.log(e)
                    })
                }
            }
        },

        fetchData() {
            this.$axios.get(`/admin_general_tests/general_tests/${this.id_test}/`, this.getHeader)
            .then(response => {
                this.title = response.data.title
                this.description = response.data.description
                this.tag = response.data.tags
                this.minimum_score = response.data.minimum_score > 0 ? response.data.minimum_score : ''
                this.publish_date = response.data.publish_date
                this.unpublish_date = response.data.unpublish_date
                this.status = response.data.status
                this.qty_user_results = response.data.qty_user_results

                if (response.data.time_to_finish) {
                    let time = response.data.time_to_finish.split(":")
                    let date = new Date()

                    date.setHours(time[0])
                    date.setMinutes(time[1])
                    date.setSeconds(time[2])

                    this.time_to_finish = date
                }

                this.profile = response.data.profiles
                this.area = response.data.areas
                this.user_group = response.data.user_groups

                if (this.user_group.length > 0) this.tab = 'groups'
                else this.tab = 'permissions'

                this.request_questions({ pagination: this.server_pagination })
                this.$q.loading.hide()
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        request_questions({pagination}) {
            this.loading = true
            this.server_pagination = pagination
            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''
            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "search": this.filter,
                "ordering": order_by
            }

            this.$axios.get(`/admin_general_tests/general_tests/${this.id_test}/general_test_questions/?${this.$qs.stringify(data)}`, this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.count
                this.questions = []

                response.data.forEach(result => {
                    this.questions.push({
                        id: result.id,
                        title: result.title,
                        qty_answers: result.general_test_question_answers.length,
                        actions: result.id
                    })
                })

                this.loading = false
            })
            .catch(e => {
                this.loading = false
                console.log(e)
            })
        },

        fetchLists() {
            this.$q.loading.show()

            this.$axios.all([this.fetchProfiles(), this.fetchAreas(), this.fetchTags(), this.fetchUserGroups()])
            .then(this.$axios.spread((profiles, areas, tags, user_groups) => {
                this.profiles = []
                this.categories = []
                this.tags = []
                this.user_groups = []

                profiles.data.forEach(profile => {
                    this.profiles.push({ value: profile.id, label: profile.name })
                })

                areas.data.country_areas.forEach(country_area => {
                    this.areas.push({ label: country_area.name, value: country_area.id })
                })

                tags.data.forEach(tag => {
                    this.tags.push({ label: tag.title, value: tag.id })
                })

                user_groups.data.forEach(user_group => {
                    this.user_groups.push({ label: user_group.title, value: user_group.id })
                })

                if (this.id_test != 0) this.fetchData()
                else this.$q.loading.hide()
            }))
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        fetchProfiles() {
            return this.$axios.get('/accounts/get_user_profiles/', this.getHeader)
        },

        fetchAreas() {
            return this.$axios.get('/accounts/get_user_areas/', this.getHeader)
        },

        fetchTags() {
            return this.$axios.get('/admin_tags/tags/get_tags/', this.getHeader)
        },

        fetchUserGroups() {
            return this.$axios.get('/accounts/get_user_group_users/', this.getHeader)
        },

        checkAll() {
            if (this.all_profiles) {
                this.profiles.forEach(element => {
                    this.profile.push(element.value)
                })
            }
            else {
                this.profile = []
            }
        },

        getErrorColor(state) {
            if (state) return 'negative'
            else return ''
        },

        getErrorClass(state) {
            if (state) return 'checkbox-error'
            else return ''
        },

        getOrder(name) {
            if (name == 'cancel') {
                if (this.$q.platform.is.mobile) return 'order-last'
                else return 'order-first'
            }
            else if (name == 'save') {
                if (this.$q.platform.is.mobile) return 'order-first'
                else return 'order-last'
            }
        },

        showQuestion(id) {
            this.$v.$touch()
            this.id_question = id

            if (!this.$v.$error) {
                this.saveTest('add_question')
            }
        },

        returnToQuestions() {
            this.add_question = false
            this.id_question = ''

            this.request_questions({
                pagination: this.server_pagination
            })
        },

        confirmRemove(id) {
            this.action_id = id
            this.confirm_remove = true
        },

        removeItem() {
            this.$q.loading.show()

            this.$axios.delete(`/admin_general_tests/general_tests/${this.action_id}/`, this.getHeader)
            .then(response => {
                this.$q.loading.hide()

                this.request_questions({
                    pagination: this.server_pagination
                })

                this.confirm_remove = false

                this.$q.notify({
                    message: this.$t('item_removed_success'),
                    type: 'positive',
                    icon: 'far fa-trash-alt'
                })
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },
    },

    validations() {
        return {
            title: { required },
            description: { required },
            tag: { required },
            time_to_finish: { required },
            minimum_score: {
                requiredIf: requiredIf(() => {
                    return this.status == 1 ? true : false
                }),
                maxValue: maxValue(this.questions.length),
                minValue: minValue(this.questions.length > 0 ? 1 : '')
            },
            area: {
                requiredIf: requiredIf(() => {
                    return this.tab == 'permissions' ? true : false
                })
            },
            profile: {
                requiredIf: requiredIf(() => {
                    return this.tab == 'permissions' ? true : false
                })
            },
            user_group: {
                requiredIf: requiredIf(() => {
                    return this.tab == 'groups' ? true : false
                })
            }
        }
    },

    created() {
        this.id_test = this.$route.params.id

        this.fetchLists()
    }
}
</script>

<style>

</style>
